import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { GroupedVirtualList, GroupedVirtualListRef } from "@ospweb/sdk-ui";
import { cn } from "utils";
import CardLoading from "../Chat/Cards/CardLoading";
import { EmptyState } from "../Empty";
import { TATimelineProps, TATimelineRef } from "./types";
import { TAItems } from "./items/TAItems";
import Translations from "@translate/components/PortfolioTimeline/TATimeline";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";
import { AgentReport } from "@/src/features/api/memberApi";

const I18n = I18nUtil.createScoped(Translations);

/**
 * TATimeline 组件
 * 技术分析时间线组件，使用 GroupedVirtualList 实现
 * 支持分组展示、吸顶效果、无限滚动、展开/折叠等功能
 */
export const TATimeline = forwardRef<TATimelineRef, TATimelineProps>(
  (
    {
      initialData = {},
      hasNextPage = false,
      onLoadMore,
      isFetchingNextPage = false,
      isLoading = false,
      isFetching = false,
      error,
      data,
      onItemClick: _onItemClick,
      stickyTopOffset = 0,
      className,
      style,
      onChartClick,
    },
    ref,
  ) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const listRef = useRef<GroupedVirtualListRef<AgentReport>>(null);
    const [isPC] = useIsPc();

    /**
     * 动态计算列表项高度
     * 根据数据内容计算不同的高度
     */
    const getItemHeight = useCallback((_: AgentReport, isExpanded: boolean) => {
      if (isExpanded) {
        return 516;
      }
      // 收起状态固定高度
      return 164;
    }, []);

    // 暴露给父组件的方法
    useImperativeHandle(
      ref,
      () => ({
        insertItem: (item: AgentReport) => {
          listRef.current?.insertItem(item);
        },
        insertItems: (items: AgentReport[]) => {
          listRef.current?.insertItems(items);
        },
        scrollToTop: (smooth?: boolean) => {
          listRef.current?.scrollToTop(smooth);
        },
        updateItemHeight: (
          itemKey: string,
          height: number,
          isExpanded?: boolean,
        ) => {
          listRef.current?.updateItemHeight(itemKey, height, isExpanded);
        },
        recalculateHeights: () => {
          listRef.current?.recalculateHeights();
        },
      }),
      [],
    );

    /**
     * 渲染列表项
     */
    const renderItem = (
      item: AgentReport,
      _index: number,
      isExpanded: boolean,
      toggleExpand: () => void,
    ) => {
      return (
        <TAItems
          item={item}
          isExpanded={isExpanded}
          toggleExpand={toggleExpand}
          isPC={isPC}
          onChartClick={onChartClick}
        />
      );
    };

    /**
     * 渲染分组标题
     */
    const renderGroupHeader = (groupKey: string) => {
      return (
        <div
          className="h-full flex items-center bg-background_solid_secondary"
          data-tracking-scope="group-header"
        >
          <h2
            aria-label="TATimeline h2"
            data-openreplay-label="TATimeline h2 Semantic"
            id="sem-tatimeline-h2"
            className="body_m text-content_tertiary"
          >
            {groupKey}
          </h2>
        </div>
      );
    };

    /**
     * 处理加载更多
     */
    const handleLoadMore = () => {
      if (!isLoading && hasNextPage && onLoadMore) {
        onLoadMore();
      }
    };

    /**
     * 渲染加载中
     */
    const renderLoading = useMemo(() => {
      if (!hasNextPage || !isFetchingNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center py-4">
          <CardLoading className="min-h-18 w-full" />
        </div>
      );
    }, [hasNextPage, isFetchingNextPage]);

    /**
     * 渲染没有更多
     */
    const renderNoMore = useMemo(() => {
      if (hasNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center py-4 pb-20">
          <span className="body_s text-content_quaternary">
            {I18n.t("No more data")}
          </span>
        </div>
      );
    }, [hasNextPage]);

    // 初始加载状态：没有数据且正在加载时显示全屏 loading
    // 使用 isFetching 判断重新请求的情况，isLoading 判断首次加载
    const isInitialLoading =
      (isLoading || isFetching) && (data?.length ?? 0) === 0;

    if (isInitialLoading) {
      return (
        <div className="flex flex-col w-full h-[calc(100vh-268px)] items-center justify-center">
          <CardLoading className="min-h-18 w-full" />
        </div>
      );
    }

    // 空状态：没有数据且不在加载时显示空状态提示
    if ((data?.length === 0 && !isLoading) || (error && data?.length === 0)) {
      return (
        <div className="flex flex-col w-full h-[calc(100vh-268px)] items-center justify-center">
          <EmptyState
            title={I18n.t("No Smart Signals Yet")}
            description={I18n.t(
              "Check other agents, I’ll alert you when this one finds something worth your attention.",
            )}
            className="w-full max-w-[320px]"
          />
        </div>
      );
    }

    return (
      <GroupedVirtualList
        ref={listRef}
        data={initialData}
        getItemKey={(item) =>
          (item?.id ?? Date.now().toString()) + (item?.agent_id ?? "")
        }
        getItemHeight={getItemHeight}
        itemGap={16}
        groupToItemGap={8}
        groupHeaderHeight={20}
        stickyTopOffset={stickyTopOffset}
        hasNextPage={hasNextPage}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={100}
        renderItem={renderItem}
        renderGroupHeader={renderGroupHeader}
        expandedItemKeys={expandedKeys}
        onItemToggle={(key, isExpanded) => {
          setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (isExpanded) {
              next.add(key);
            } else {
              next.delete(key);
            }
            return next;
          });
        }}
        insertAnimation="fade"
        checkDuplicateGroups={3}
        expandTransitionDuration={300}
        renderLoading={renderLoading}
        renderNoMore={renderNoMore}
        className={cn(
          "md:h-[calc(100vh-148px)] h-[calc(100vh-248px)]",
          className,
        )}
        style={style}
      />
    );
  },
);

TATimeline.displayName = "TATimeline";
